import React, { useState } from 'react';

// Merchants Table Component
export const MerchantsTable = ({ merchants, onEdit, onDelete, onAdd, isDarkMode }) => (
  <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
    <div className="flex justify-between items-center mb-4">
      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Merchants ({merchants.length})</h3>
      <button
        onClick={onAdd}
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold"
      >
        + Add Merchant
      </button>
    </div>
    
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
            <th className="px-4 py-3 text-left">Avatar</th>
            <th className="px-4 py-3 text-left">Name</th>
            <th className="px-4 py-3 text-left">Specialty</th>
            <th className="px-4 py-3 text-left">Greeting (English)</th>
            <th className="px-4 py-3 text-left">Status</th>
            <th className="px-4 py-3 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {merchants.map(merchant => (
            <tr key={merchant.id} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <td className="px-4 py-3">
                {merchant.image_url && merchant.image_url.startsWith('http') ? (
                  <img src={merchant.image_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <span className="text-2xl">{merchant.avatar || '👤'}</span>
                )}
              </td>
              <td className={`px-4 py-3 font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{merchant.name}</td>
              <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{merchant.specialty}</td>
              <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{merchant.greeting_english?.substring(0, 50)}...</td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 rounded-full text-xs ${merchant.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {merchant.is_active !== false ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={() => onEdit(merchant)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded mr-2 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(merchant)}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Categories Table Component
export const CategoriesTable = ({ categories, onEdit, onDelete, onAdd, isDarkMode }) => (
  <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
    <div className="flex justify-between items-center mb-4">
      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Item Categories ({categories.length})</h3>
      <button
        onClick={onAdd}
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold"
      >
        + Add Category
      </button>
    </div>
    
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
            <th className="px-4 py-3 text-left">ID</th>
            <th className="px-4 py-3 text-left">Name</th>
            <th className="px-4 py-3 text-left">Icon</th>
            <th className="px-4 py-3 text-left">Order</th>
            <th className="px-4 py-3 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {categories.map(category => (
            <tr key={category.id} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <td className={`px-4 py-3 font-mono text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{category.id}</td>
              <td className={`px-4 py-3 font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{category.name}</td>
              <td className="px-4 py-3 text-2xl">{category.icon || '📁'}</td>
              <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{category.order || 0}</td>
              <td className="px-4 py-3">
                <button
                  onClick={() => onEdit(category)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded mr-2 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(category)}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Items Table Component
export const ItemsTable = ({ items, merchants, onEdit, onDelete, onAdd, isDarkMode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  const categories = [...new Set(items.map(item => item.category))];
  const filteredItems = items.filter(item => {
    const matchesSearch = (item.name_english || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.name_darija || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Clothing Items ({items.length})</h3>
        <button
          onClick={onAdd}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold"
        >
          + Add Item
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <input
          type="text"
          placeholder="Search items..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`flex-1 px-4 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
        />
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className={`px-4 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
        >
          <option value="all">All Categories</option>
          {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <th className="px-4 py-3 text-left">Image</th>
              <th className="px-4 py-3 text-left">Name (English)</th>
              <th className="px-4 py-3 text-left">Name (Darija)</th>
              <th className="px-4 py-3 text-left">Category</th>
              <th className="px-4 py-3 text-left">Price</th>
              <th className="px-4 py-3 text-left">Merchant</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map(item => (
              <tr key={item.id} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <td className="px-4 py-3">
                  {item.image_url ? (
                    <img 
                      src={item.image_url} 
                      alt={item.name_english}
                      className="w-16 h-16 object-cover rounded border-2 border-gray-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <div 
                    className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded border-2 border-gray-300 text-3xl"
                    style={{display: item.image_url ? 'none' : 'flex'}}
                  >
                    {item.icon || '👕'}
                  </div>
                </td>
                <td className={`px-4 py-3 font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{item.name_english || 'N/A'}</td>
                <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{item.name_darija || 'N/A'}</td>
                <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{item.category}</td>
                <td className={`px-4 py-3 font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{item.base_price_tokens || item.base_price_dh || 0} Tokens</td>
                <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {merchants.find(m => m.id === item.merchant_id)?.name || 'N/A'}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onEdit(item)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded mr-2 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(item)}
                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Missions Table Component
export const MissionsTable = ({ missions, items, onEdit, onAdd, onDelete, isDarkMode }) => (
  <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
    <div className="flex justify-between items-center mb-4">
      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Outfit Missions ({missions.length})</h3>
      {onAdd && (
        <button onClick={onAdd} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold">
          + Add Mission
        </button>
      )}
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {missions.map(mission => (
        <div key={mission.id} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="text-3xl">{mission.icon || '🎯'}</div>
              {mission.image_url && (
                <img
                  src={mission.image_url}
                  alt={mission.name_english}
                  className="w-12 h-12 object-cover rounded"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
            </div>
            <div className="flex gap-1">
              <button onClick={() => onEdit(mission)} className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm">Edit</button>
              {onDelete && (
                <button onClick={() => onDelete(mission)} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm">Del</button>
              )}
            </div>
          </div>
          <h4 className={`font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{mission.name_english}</h4>
          <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{mission.name_darija}</p>
          <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{mission.description}</p>
          <div className="space-y-1 text-sm">
            <div className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
              <strong>Occasion:</strong> {mission.occasion}
            </div>
            <div className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
              <strong>Difficulty:</strong> {mission.difficulty}
            </div>
            <div className={`font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
              💰 {mission.reward_coins} Coins | 🎯 {mission.reward_xp} XP
            </div>
            {(mission.time_limit_seconds > 0 || mission.budget_limit > 0) && (
              <div className={`text-xs ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                {mission.time_limit_seconds > 0 && `⏱️ ${Math.floor(mission.time_limit_seconds/60)}:${(mission.time_limit_seconds%60).toString().padStart(2,'0')}`}
                {mission.time_limit_seconds > 0 && mission.budget_limit > 0 && ' | '}
                {mission.budget_limit > 0 && `💰 Budget: ${mission.budget_limit}`}
              </div>
            )}
            {mission.required_items?.length > 0 && (
              <div className={`text-xs ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                🧥 {mission.required_items.length} required item{mission.required_items.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Souk Areas Table Component
export const AreasTable = ({ areas, onEdit, onDelete, onAdd, isDarkMode }) => (
  <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
    <div className="flex justify-between items-center mb-4">
      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Souk Areas ({areas.length})</h3>
      <button onClick={onAdd} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold">
        + Add Area
      </button>
    </div>

    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
            <th className="px-4 py-3 text-left">Icon</th>
            <th className="px-4 py-3 text-left">Name</th>
            <th className="px-4 py-3 text-left">Darija</th>
            <th className="px-4 py-3 text-left">Unlock Req</th>
            <th className="px-4 py-3 text-left">Order</th>
            <th className="px-4 py-3 text-left">Active</th>
            <th className="px-4 py-3 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {areas.map(area => (
            <tr key={area.id} className={`border-t ${isDarkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'}`}>
              <td className="px-4 py-3 text-2xl">{area.icon || '🗺️'}</td>
              <td className={`px-4 py-3 font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{area.name}</td>
              <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{area.name_darija || '-'}</td>
              <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{area.unlock_requirement || 0} missions</td>
              <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{area.order || 0}</td>
              <td className="px-4 py-3">{area.is_active !== false ? '✅' : '❌'}</td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  <button onClick={() => onEdit(area)} className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm">Edit</button>
                  <button onClick={() => onDelete(area)} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm">Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {areas.length === 0 && (
      <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No souk areas yet. Click "+ Add Area" to create your first area.
      </div>
    )}
  </div>
);

// Users Table Component
export const UsersTable = ({ users, onAddCoins, onEditCoins, onToggleWallet, isDarkMode }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterType, setFilterType] = React.useState('all');

  // Filter logic
  const filteredUsers = users.filter(user => {
    // Search filter
    const matchesSearch = !searchTerm || 
      (user.user_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.user_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Type filter
    let matchesType = true;
    if (filterType === 'active') matchesType = (user.coins !== undefined && user.coins > 0) || (user.owned_items?.length > 0);
    if (filterType === 'never_played') matchesType = !user.coins && !user.owned_items?.length;
    if (filterType === 'paid') matchesType = user.is_paid_user === true;
    if (filterType === 'free') matchesType = user.is_paid_user !== true;
    if (filterType === 'locked') matchesType = user.wallet_locked === true;
    
    return matchesSearch && matchesType;
  });

  return (
    <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          User Wallets ({filteredUsers.length} / {users.length})
        </h3>
      </div>

      {/* Search and Filters */}
      <div className="mb-4 space-y-3">
        {/* Search Bar */}
        <input
          type="text"
          placeholder="🔍 Search by email or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`w-full px-4 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-500'}`}
        />

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'all', label: 'All Users', icon: '👥' },
            { id: 'active', label: 'Active Players', icon: '🎮' },
            { id: 'never_played', label: 'Never Played', icon: '💤' },
            { id: 'paid', label: 'Paid Users', icon: '👑' },
            { id: 'free', label: 'Free Users', icon: '🆓' },
            { id: 'locked', label: 'Locked Wallets', icon: '🔒' }
          ].map(filter => (
            <button
              key={filter.id}
              onClick={() => setFilterType(filter.id)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                filterType === filter.id
                  ? 'bg-purple-600 text-white'
                  : isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.icon} {filter.label}
            </button>
          ))}
        </div>
      </div>
    
    {filteredUsers.length === 0 ? (
      <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <p>No users match your search criteria.</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <th className="px-4 py-3 text-left">User</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Level</th>
              <th className="px-4 py-3 text-left">XP</th>
              <th className="px-4 py-3 text-left">Tokens</th>
              <th className="px-4 py-3 text-left">Items</th>
              <th className="px-4 py-3 text-left">Outfits</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => {
              const hasPlayed = user.coins !== undefined || (user.owned_items?.length > 0);
              const isPaid = user.is_paid_user === true;
              const isLocked = user.wallet_locked === true;
              
              return (
                <tr key={user.user_id} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} ${isLocked ? 'opacity-60' : ''}`}>
                  <td className={`px-4 py-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    <div className="font-medium">{user.user_email || 'Unknown'}</div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user.user_name || 'Unknown User'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {isPaid && <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs rounded">👑 Paid</span>}
                      {!isPaid && <span className="px-2 py-0.5 bg-gray-500 text-white text-xs rounded">🆓 Free</span>}
                      {hasPlayed && <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded">🎮</span>}
                      {!hasPlayed && <span className="px-2 py-0.5 bg-gray-400 text-white text-xs rounded">💤</span>}
                      {isLocked && <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded">🔒</span>}
                    </div>
                  </td>
                  <td className={`px-4 py-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>⭐ {user.level || 1}</td>
                  <td className={`px-4 py-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>🎯 {user.xp || 0}</td>
                  <td className={`px-4 py-3 font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>💰 {user.coins || 0}</td>
                  <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{user.owned_items?.length || 0}</td>
                  <td className={`px-4 py-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{user.completed_outfits?.length || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        onClick={() => onEditCoins(user)}
                        disabled={isLocked}
                        className={`px-2 py-1 rounded text-xs ${isLocked ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                        title={isLocked ? 'Wallet is locked' : 'Edit coins'}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => onAddCoins(user)}
                        disabled={isLocked}
                        className={`px-2 py-1 rounded text-xs ${isLocked ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white`}
                        title={isLocked ? 'Wallet is locked' : 'Add coins'}
                      >
                        +
                      </button>
                      <button
                        onClick={() => onToggleWallet(user)}
                        className={`px-2 py-1 rounded text-xs ${isLocked ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'} text-white`}
                        title={isLocked ? 'Unlock wallet' : 'Lock wallet'}
                      >
                        {isLocked ? '🔓' : '🔒'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
    </div>
  );
};

// Bargain Text Editor Component
export const BargainEditor = ({ texts, onSave, isDarkMode }) => {
  const [formData, setFormData] = useState({
    ...texts,
    initial_welcome_texts: texts.initial_welcome_texts || ["Ahlan wa sahlan! Welcome my friend!"],
    first_counter_texts: texts.first_counter_texts || ["Mabrouk! Excellent choice!"],
    player_thinking_texts: texts.player_thinking_texts || ["La, la, la my friend! Don't think too much!"],
    ask_offer_texts: texts.ask_offer_texts || ["So you want to make your own offer?"],
    final_counter_texts: texts.final_counter_texts || ["Ahhh you are tough negotiator!"],
    success_texts: texts.success_texts || ["Mabrouk! You got excellent deal!"],
    rejection_texts: texts.rejection_texts || ["La, la, my friend. That price is too low."],
    too_low_warning_texts: texts.too_low_warning_texts || ["Wallah you are killing me with that price!"]
  });

  const updateTextArray = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({...formData, [field]: newArray});
  };

  const addText = (field) => {
    setFormData({...formData, [field]: [...formData[field], '']});
  };

  const removeText = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({...formData, [field]: newArray});
  };

  const TextSection = ({ title, field, placeholder, description }) => (
    <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <label className={`block font-medium mb-1 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{title}</label>
      {description && (
        <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{description}</p>
      )}
      {formData[field]?.map((text, index) => (
        <div key={index} className="flex gap-1 mb-1">
          <textarea
            value={text}
            onChange={(e) => updateTextArray(field, index, e.target.value)}
            rows="2"
            placeholder={placeholder}
            className={`flex-1 px-2 py-1 rounded-lg border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
          />
          <button
            onClick={() => removeText(field, index)}
            className="px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 text-xs h-fit"
          >
            ✕
          </button>
        </div>
      ))}
      <button
        onClick={() => addText(field)}
        className="mt-1 px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 text-xs"
      >
        + Add Variation
      </button>
    </div>
  );

  return (
    <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Bargaining Dialogue Texts</h3>
      
      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
        <TextSection 
          title="👋 Initial Welcome (When Bargaining Starts)" 
          field="initial_welcome_texts" 
          placeholder="Ahlan wa sahlan! Welcome my friend! You have excellent taste..."
          description="Variables: {item} = item name, {price} = base price"
        />

        <TextSection 
          title="💰 First Counter Offer (With Discount)" 
          field="first_counter_texts" 
          placeholder="Mabrouk! Excellent choice! Special discount: {price} Coins..."
          description="Variables: {price} = discounted price, {discount} = discount percentage"
        />

        <TextSection 
          title="🤔 Player Thinking (When Player Hesitates)" 
          field="player_thinking_texts" 
          placeholder="La, la, la! Don't think too much! Best deal in Marrakech..."
          description="Used when player doesn't respond quickly"
        />

        <TextSection 
          title="❓ Ask for Player Offer" 
          field="ask_offer_texts" 
          placeholder="So you want to make your own offer? Tell me your price..."
          description="Prompts player to make a counter-offer"
        />

        <TextSection 
          title="🔥 Final Counter (Last Offer)" 
          field="final_counter_texts" 
          placeholder="Okay wallah! Final price: {price} Coins! Cannot go lower..."
          description="Variables: {price} = final price, {discount} = final discount"
        />

        <TextSection 
          title="✅ Success (Deal Accepted)" 
          field="success_texts" 
          placeholder="Mabrouk! You got excellent deal! Here is your {item}..."
          description="Variables: {item} = item name"
        />

        <TextSection 
          title="❌ Rejection (Price Too Low)" 
          field="rejection_texts" 
          placeholder="La, la! That price is too low. Maybe another time..."
          description="When player's offer is rejected"
        />

        <TextSection 
          title="⚠️ Too Low Warning (Before Rejection)" 
          field="too_low_warning_texts" 
          placeholder="Wallah you are killing me! That's too low..."
          description="Warning before final rejection"
        />

        {/* Discount Controls */}
        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-orange-700' : 'bg-orange-50 border-orange-300'}`}>
          <h4 className={`font-bold mb-2 text-sm ${isDarkMode ? 'text-orange-300' : 'text-orange-800'}`}>🎯 Merchant Discount Control</h4>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Min Discount %</label>
              <input
                type="number"
                value={formData.merchant_min_discount || 10}
                onChange={(e) => setFormData({...formData, merchant_min_discount: parseInt(e.target.value)})}
                min="0"
                max="100"
                className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Max Discount %</label>
              <input
                type="number"
                value={formData.merchant_max_discount || 35}
                onChange={(e) => setFormData({...formData, merchant_max_discount: parseInt(e.target.value)})}
                min="0"
                max="100"
                className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Acceptance %</label>
              <input
                type="number"
                value={formData.default_acceptance_threshold || 75}
                onChange={(e) => setFormData({...formData, default_acceptance_threshold: parseInt(e.target.value)})}
                min="0"
                max="100"
                className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
          </div>
          <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            💡 Merchants will accept offers between {formData.merchant_min_discount || 10}% - {formData.merchant_max_discount || 35}% off. 
            Acceptance threshold: if offer is ≥{formData.default_acceptance_threshold || 75}% of counter price, merchant accepts.
          </p>
        </div>

        <button
          onClick={() => onSave(formData)}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-bold text-sm"
        >
          Save Bargaining Texts
        </button>
      </div>
    </div>
  );
};

// Continue with modals in next file...
